import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Fetch QuickBooks connection status for a specific clinic
export const fetchQboConnectionStatus = createAsyncThunk(
  "qbo/fetchConnectionStatus",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      // Validate clinicId
      if (!clinicId || clinicId === "" || clinicId === "undefined") {
        return rejectWithValue("Invalid clinic ID provided");
      }

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/qbConnected/${clinicId}`, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Start QuickBooks OAuth connection for a specific clinic
export const startQboOAuth = createAsyncThunk(
  "qbo/startOAuth",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      // Validate clinicId
      if (!clinicId || clinicId === "" || clinicId === "undefined") {
        return rejectWithValue("Invalid clinic ID provided");
      }

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(`${baseURL}/api/startQuickBooksOAuth/${clinicId}`, config);

      if (response.data?.authUrl) {
        window.location.href = response.data.authUrl; // Redirect to QuickBooks OAuth URL
        return { status: "redirecting" }; // Optional return for tracking
      } else {
        throw new Error("Auth URL not provided in response");
      }
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Disconnect QuickBooks for a specific clinic
export const disconnectQbo = createAsyncThunk(
  "qbo/disconnect",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      // Validate clinicId
      if (!clinicId || clinicId === "" || clinicId === "undefined") {
        return rejectWithValue("Invalid clinic ID provided");
      }

      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.delete(`${baseURL}/api/qbConnected/${clinicId}`, config);
      return response.data; // Return success response if needed
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Update QuickBooks sync settings for a specific clinic
export const updateQboSyncSettings = createAsyncThunk(
  "qbo/updateSyncSettings",
  async ({ clinicId, settings }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.patch(
        `${baseURL}/api/qbConnected/${clinicId}/settings`,
        settings,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch QuickBooks data (example: fetch invoices) for a specific clinic
export const fetchQboInvoices = createAsyncThunk(
  "qbo/fetchInvoices",
  async ({ clinicId, page = 1, limit = 10 }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/qbInvoices/${clinicId}?page=${page}&limit=${limit}`,
        config
      );
      return {
        invoices: response.data.invoices,
        pagination: response.data.pagination,
      };
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Manual sync actions for specific clinic
export const syncPendingInvoices = createAsyncThunk(
  "qbo/syncPendingInvoices",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/qbSync/${clinicId}/invoices`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const syncPendingPayments = createAsyncThunk(
  "qbo/syncPendingPayments",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/qbSync/${clinicId}/payments`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const testQboConnection = createAsyncThunk(
  "qbo/testConnection",
  async (clinicId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/qbConnected/${clinicId}/test`,
        config
      );
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
